import type {
  RankedSearchResult,
  SearchDocument,
} from './searchTypes';

const WEAK_FUSED_SCORE = 0.018;
const WEAK_SEMANTIC_SCORE = 0.3;

function normalized(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function distance(first: string, second: string): number {
  const previous = Array.from(
    { length: second.length + 1 },
    (_, index) => index,
  );
  for (let firstIndex = 1; firstIndex <= first.length; firstIndex += 1) {
    let diagonal = previous[0];
    previous[0] = firstIndex;
    for (let secondIndex = 1; secondIndex <= second.length; secondIndex += 1) {
      const above = previous[secondIndex];
      previous[secondIndex] = Math.min(
        previous[secondIndex] + 1,
        previous[secondIndex - 1] + 1,
        diagonal +
          (first[firstIndex - 1] === second[secondIndex - 1] ? 0 : 1),
      );
      diagonal = above;
    }
  }
  return previous[second.length];
}

function fuzzyScore(query: string, candidate: string): number {
  const normalizedQuery = normalized(query);
  const normalizedCandidate = normalized(candidate);
  const queryWords = normalizedQuery.split(' ');
  const candidateWords = normalizedCandidate.split(' ');
  const whole = distance(normalizedQuery, normalizedCandidate);
  const word = Math.min(
    ...queryWords.flatMap((queryWord) =>
      candidateWords.map((candidateWord) => distance(queryWord, candidateWord)),
    ),
  );
  return Math.min(whole, word);
}

export function isWeakResults(
  results: readonly RankedSearchResult[],
): boolean {
  if (results.length < 3) return true;
  const first = results[0];
  if (first.semanticScore !== undefined) {
    return first.semanticScore < WEAK_SEMANTIC_SCORE;
  }
  return first.score < WEAK_FUSED_SCORE;
}

export function relatedTopics(
  nearest: readonly RankedSearchResult[],
  limit = 4,
): string[] {
  const topics: string[] = [];
  for (const result of nearest) {
    for (const theme of result.document.themes) {
      if (!topics.includes(theme)) topics.push(theme);
      if (topics.length >= limit) return topics;
    }
  }
  return topics;
}

export function didYouMean(
  query: string,
  documents: readonly SearchDocument[],
  limit = 3,
): string[] {
  const candidates = new Set<string>();
  for (const document of documents) {
    candidates.add(document.speaker);
    candidates.add(document.title);
  }
  const queryLength = normalized(query).length;
  const maximum = Math.max(2, Math.floor(queryLength * 0.3));
  return [...candidates]
    .map((value) => ({ value, score: fuzzyScore(query, value) }))
    .filter(({ score }) => score > 0 && score <= maximum)
    .sort(
      (first, second) =>
        first.score - second.score || first.value.localeCompare(second.value),
    )
    .slice(0, limit)
    .map(({ value }) => value);
}
